<template>
  <div class="plano-contas-table">
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :data="planos"
      :auto-load="false"
      :refreshButton="refreshButton"
      data-testid="plano-contas-table"
      @button-click="handleButtonClick"
      @edit-click="handleEditClick"
      @refresh="emit('refresh')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { permissionsService } from '../../services/permissionsService';

type PlanoId = number | string;

interface Plano {
  id: PlanoId;
  franquia_id?: PlanoId;
  franquia?: string;
  nome?: string;
  is_active?: boolean;
  total_itens?: number;
  fixas_count?: number;
  variaveis_count?: number;
  created_at?: string;
  status?: string;
  [key: string]: unknown;
}

interface RefreshButtonConfig {
  show?: boolean;
  [key: string]: unknown;
}

interface DataTableInstance {
  loadData: () => void;
}

interface CellRendererParams {
  data: Plano;
  value?: unknown;
}

interface ButtonClickPayload {
  value?: unknown;
  data: Plano;
  action: string;
}

const { t } = useI18n();

const props = withDefaults(defineProps<{
  planos?: Plano[];
  loading?: boolean;
  refreshButton?: RefreshButtonConfig | null;
}>(), {
  planos: () => [],
  loading: false,
  refreshButton: null
});

const emit = defineEmits<{
  clone: [plano: Plano];
  'toggle-status': [payload: { data: Plano; newValue: boolean }];
  refresh: [];
  'edit-click': [id: PlanoId];
}>();

const datatableRef = ref<DataTableInstance | null>(null);

const canClone = computed<boolean>(() => {
  return permissionsService.isUserInGroups(['Full']);
});

const tableColumns = computed(() => {
  const columns: Array<Record<string, unknown>> = [
    {
      data: 'id',
      title: t('ID'),
      sortable: true,
      renderType: 'clickable_id',
      mergedColumns: [
        {
          data: 'franquia',
          title: t('Franquia')
        }
      ]
    },
    {
      data: 'nome',
      title: t('Plano'),
      sortable: true,
      width: 200,
      cellRenderer: (params: CellRendererParams) => {
        return `<strong>${params.data.nome || t('Plano sem nome')}</strong>`;
      }
    },
    {
      data: 'is_active',
      title: t('Status'),
      width: 100,
      sortable: true,
      renderType: 'toggle',
      toggleCallback: (rowData: Plano, newValue: boolean) => {
        emit('toggle-status', { data: rowData, newValue });
      }
    },
    {
      data: 'total_itens',
      title: t('Total Itens'),
      width: 110,
      sortable: true,
      renderType: 'integer',
      mergedColumns: [
        {
          data: 'fixas_count',
          title: t('Fixas'),
          cellRenderer: (params: CellRendererParams) =>
            `${t('Fixas')}: ${params.data.fixas_count ?? 0}`
        },
        {
          data: 'variaveis_count',
          title: t('Variáveis'),
          cellRenderer: (params: CellRendererParams) =>
            `${t('Variáveis')}: ${params.data.variaveis_count ?? 0}`
        }
      ]
    },
    {
      data: 'created_at',
      title: t('Criado em'),
      width: 160,
      sortable: true,
      renderType: 'datetime',
      dataType: 'date'
    }
  ];

  const actionButtons: Array<Record<string, unknown>> = [];

  if (canClone.value) {
    actionButtons.push({
      text: '',
      icon: 'pi pi-clone',
      class: 'p-button-text p-button-sm',
      tooltip: t('Clonar'),
      action: 'clone',
      'data-testid': 'plano-contas-clone-button'
    });
  }

  if (actionButtons.length > 0) {
    columns.push({
      data: null,
      title: t('Ações'),
      width: 80,
      sortable: false,
      renderType: 'action_buttons',
      buttons: actionButtons
    });
  }

  return columns;
});

const datatableConfig = computed(() => {
  return {
    tableId: 'planoContasDataTable',
    columns: tableColumns.value,
    pagination: false,
    sortable: true,
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const handleButtonClick = ({ data, action }: ButtonClickPayload): void => {
  switch (action) {
    case 'clone':
      emit('clone', data);
      break;
    default:
      console.warn('Unknown action:', action);
  }
};

const handleEditClick = (id: PlanoId): void => {
  emit('edit-click', id);
};

defineExpose({
  loadData: (): void => datatableRef.value?.loadData()
});
</script>

<style scoped>
.plano-contas-table :deep(.action-buttons-cell) {
  display: flex;
  gap: 2px;
}
</style>
